<template>
  <div>
    <!-- بطاقات المدرّسين -->
    <div class="row g-5 g-xl-10 mb-5">
      <div v-if="slotsLoading" class="text-center w-100 my-4">
        <div class="spinner-border text-success" role="status">
          <span class="visually-hidden">{{ $t("common.loading") }}</span>
        </div>
        <div class="mt-2">{{ $t("prv.loading_sessions") }}</div>
      </div>
      <div
        v-for="ins in instructors"
        :key="ins.id"
        class="col-md-6"
        style="margin-top: 0px"
      >
        <!-- بطاقة -->
        <div class="card mb-5 mb-xl-8 mt-5 shadow-sm">
          <div class="card-body pb-0">
            <!-- Header -->
            <div class="d-flex align-items-center mb-5">
              <div class="d-flex align-items-center flex-grow-1">
                <!-- Avatar -->
                <div class="symbol symbol-50px me-5">
                  <img
                    :src="imageUrl(ins.instructor_profile.user_img)"
                    :alt="ins.name"
                    @error="onImageError"
                  />
                </div>
                <!-- Info -->
                <div class="d-flex flex-column">
                  <span class="text-gray-900 fs-6 fw-bold text-hover-primary">
                    {{ ins.name }}
                  </span>
                  <!-- عناوين المواد -->
                </div>
              </div>
              <!-- Rating -->
              <div class="rating my-0">
                <template v-for="(filled, idx) in stars(ins.rating)" :key="idx">
                  <div class="rating-label me-1" :class="{ checked: filled }">
                    <i class="bi bi-star-fill fs-5"></i>
                  </div>
                </template>
              </div>
            </div>
            <!-- Post -->
            <div class="mb-5">
              <div class="row">
                <div class="col-md-8">
                  <div class="text-gray-800 fw-normal mb-5">
                    <div>
                      <b>{{ $t("prv.session_date") }} :</b>
                      <span style="font-size: 12px">{{
                        formatDateTime(ins.start_date)
                      }}</span>
                    </div>
                    <div>
                      <b>{{ $t("prv.lesson_type") }} :</b>
                      <span>{{
                        ins.type === "video" ? $t("prv.type.video") : ins.type
                      }}</span>
                    </div>
                  </div>
                </div>

                <div class="col-md-4 text-center">
                  <span
                    v-if="isLive(ins)"
                    class="badge badge-secondary mb-1"
                    style="font-size: 9px; font-weight: 700"
                  >
                    {{ $t("prv.live_now") }}
                  </span>

                  <span
                    class="badge badge-secondary mb-1"
                    style="margin-right: -10px"
                  >
                    {{ getDuration(ins.start_date, ins.end_date) }}
                  </span>

                  <!-- إذا الجلسة جارية الآن -->
                  <button
                    v-if="ins.isLiveNow"
                    class="btn btn-success btn-sm mt-1"
                    style="font-weight: 700; font-size: 11px"
                    @click="
                      openLiveSessionInNewTab(
                        ins.id,
                        ins.instructor_profile.instructor_id,
                        ins.instructor_profile.user.fname,
                        ins.instructor_profile.user.lname
                      )
                    "
                  >
                    {{ $t("prv.join_now") }}
                  </button>

                  <!-- إذا الجلسة لم تبدأ بعد: عرض زر استرجاع -->
                  <div v-else class="mt-1">
                    <button
                      class="btn btn-outline-danger btn-sm"
                      style="font-weight: 700; font-size: 11px"
                      @click="cancelReservation(ins.id)"
                    >
                      {{ $t("prv.refund") }}
                    </button>
                    <div
                      class="text-muted mt-1"
                      style="font-size: 11px; font-weight: 600"
                    >
                      {{ ins.countdown }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="separator mb-4"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- الترحيل بين الصفحات -->
    <nav
      v-if="pagerMeta.total > pagerMeta.per_page"
      aria-label="صفحات المدرّسين"
    >
      <ul class="pagination justify-content-center">
        <!-- Previous -->
        <li class="page-item" :class="{ disabled: !pagerLinks.prev }">
          <a
            class="page-link"
            href="#"
            @click.prevent="goToPage(pagerLinks.prev)"
            v-html="'&laquo;'"
          ></a>
        </li>

        <!-- أرقام الصفحات -->
        <li
          v-for="link in pagerMeta.links"
          :key="link.label"
          class="page-item"
          :class="{
            active: link.active,
            disabled: !link.url,
          }"
        >
          <a
            v-if="link.url"
            class="page-link"
            href="#"
            @click.prevent="goToPage(link.url)"
            v-html="link.label"
          ></a>
          <span v-else class="page-link" v-html="link.label"></span>
        </li>

        <!-- Next -->
        <li class="page-item" :class="{ disabled: !pagerLinks.next }">
          <a
            class="page-link"
            href="#"
            @click.prevent="goToPage(pagerLinks.next)"
            v-html="'&raquo;'"
          ></a>
        </li>
      </ul>
    </nav>
  </div>
  <!-- Modal الفيديو -->
  <!-- Modal كبير -->
  <!-- Modal كبير -->
  <div class="modal fade" id="videoSessionModal" tabindex="-1">
    <div
      class="modal-dialog modal-xl modal-dialog-centered"
      style="max-width: 95%"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">الجلسة المباشرة</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
          ></button>
        </div>
        <div class="modal-body p-0">
          <LiveSession
            v-if="session"
            :token="session.token"
            :channel-name="session.channelName"
            :uid="session.uid"
            :app-id="appId"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiUrl, baseUrl } from "@/utils/api";
import LiveSession from "./LiveSession.vue";

export default {
  name: "FutureSessionsList",
  components: { LiveSession },
  props: {
    session: Object,
  },
  data() {
    return {
      /* --------- قائم كما هو --------- */
      categoriesData: [],
      selectedCategoryId: null,
      selectedSubCategoryId: null,
      selectedChildCategoryId: null,
      isLoading: true,
      slotsLoading: false,
      isLoggedIn: false,
      token: localStorage.getItem("access_token"),
      session: null,
      appId: "2e66a5f41b2f4200babc3e87b64efa39", // عوضها بـ App ID
      /* --------- جديد --------- */
      instructors: [], // البيانات التى سنعرضها
      pagerLinks: {}, // prev / next / first / last
      pagerMeta: {}, // current_page, links[], ...
      instructorImgPath: baseUrl + "/images/instructors/", // عدِّل المسار إذا لزم
      countdown: "",
      interval: null,
      /* أي شيء آخر */
      defaultImage: "/assets/img/acad-icon.png",
    };
  },

  computed: {
    /** التخصصات حسب الفئة المختارة */
  },

  mounted() {
    this.getAvailableSlots().then(() => {
      this.startCountdowns(); // ابدأ العد التنازلي بعد تحميل البيانات
    });
  },
  beforeUnmount() {
    if (this.interval) clearInterval(this.interval);
  },
  methods: {
    startCountdowns() {
      this.interval = setInterval(() => {
        const now = new Date();
        this.instructors.forEach((ins) => {
          const start = new Date(ins.start_date);
          const end = new Date(ins.end_date);
          const diffMs = start - now;

          // حدثت الجلسة أو بدأت الآن
          if (diffMs <= 0) {
            ins.countdown = "بدأت الجلسة";
            ins.isLiveNow = now >= start && now <= end;
            return;
          }

          ins.isLiveNow = false; // قبل بدء الجلسة

          const totalSeconds = Math.floor(diffMs / 1000);
          const hours = Math.floor(totalSeconds / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          const seconds = totalSeconds % 60;

          if (hours > 0) {
            ins.countdown = `ستبدأ الجلسة بعد ${hours} ساعة و ${minutes} دقيقة و ${seconds} ثانية`;
          } else if (minutes > 0) {
            ins.countdown = `ستبدأ الجلسة بعد ${minutes} دقيقة و ${seconds} ثانية`;
          } else {
            ins.countdown = `ستبدأ الجلسة بعد ${seconds} ثانية`;
          }
        });
      }, 1000);
    },

    async getAvailableSlots(pageUrl = null) {
      this.slotsLoading = true;

      const endpoint = pageUrl || `${apiUrl}/live-sessions/my-slots`;

      const payload = {
        slots_type: "future",
      };

      try {
        const res = await fetch(endpoint, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        if (!res.ok) throw new Error(res.status);
        this.slotsLoading = false;
        const json = await res.json();
        /* بيانات الـ API مُهيكلة هكذا: { instructors: { data: [], links:{}, meta:{} } } */
        const wrapper = json.future_slots;

        this.instructors = wrapper.data;
        this.pagerLinks = wrapper.links;
        this.pagerMeta = wrapper;
      } catch (err) {
        console.error("❌ getAvailableSlots:", err);
      } finally {
        this.slotsLoading = false;
      }
    },

    /* ---------- الإنتقال بين الصفحات ---------- */
    async goToPage(url) {
      if (!url) return;
      // الـ API يقبل استدعاء GET بالرابط الكامل، لذلك نستخدمه كما هو
      await this.getAvailableSlots(url);
    },

    /* ---------- مساعدات صغيرة للعرض ---------- */
    imageUrl(img) {
      return img ? img : this.defaultImage;
    },
    onImageError(event) {
      event.target.src = this.defaultImage;
    },
    /* يحوِّل التاريخ إلى 07/07/2025 12:30 */
    formatDateTime(dt) {
      const d = new Date(dt.replace(" ", "T")); // يدعم Safari
      return (
        d.toLocaleDateString("en-US") +
        " " +
        d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
      );
    },
    /* يحوّل 3.7 إلى مصفوفة طولها 5 لتلوين النجوم */
    stars(rating) {
      const r = Math.round(Number(rating) || 0);
      return Array.from({ length: 5 }, (_, i) => i < r);
    },
    /* هل الجلسة حالياً جارية؟ */
    timeUntilStart(session) {
      const now = new Date();
      const start = new Date(session.start_date);
      const diffMs = start - now;

      if (diffMs <= 0) return "بدأت الجلسة";

      const minutes = Math.floor(diffMs / 60000);
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;

      if (hours > 0) {
        return `ستبدأ الجلسة بعد ${hours} ساعة و ${remainingMinutes} دقيقة`;
      } else {
        return `ستبدأ الجلسة بعد ${remainingMinutes} دقيقة`;
      }
    },
    updateCountdown() {
      const now = new Date();
      const start = new Date(this.session.start_date);
      const diffMs = start - now;

      if (diffMs <= 0) {
        this.countdown = "بدأت الجلسة";
        clearInterval(this.interval); // توقف التحديث إذا بدت الجلسة
        return;
      }

      const totalSeconds = Math.floor(diffMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      if (hours > 0) {
        this.countdown = `ستبدأ الجلسة بعد ${hours} ساعة و ${minutes} دقيقة و ${seconds} ثانية`;
      } else if (minutes > 0) {
        this.countdown = `ستبدأ الجلسة بعد ${minutes} دقيقة و ${seconds} ثانية`;
      } else {
        this.countdown = `ستبدأ الجلسة بعد ${seconds} ثانية`;
      }
    },

    isLive(session) {
      const now = new Date();
      const start = new Date(session.start_date);
      const end = new Date(session.end_date);
      return now >= start && now <= end;
    },
    timeRange(slot) {
      const s = new Date(slot.start_date);
      const e = new Date(slot.end_date);
      const opts = { hour: "2-digit", minute: "2-digit" };
      return `من ${s.toLocaleTimeString(
        "en-US",
        opts
      )} إلى ${e.toLocaleTimeString("en-US", opts)}`;
    },
    async cancelReservation(slotId) {
      const { value: reason } = await Swal.fire({
        title: this.$t("prv.refund_reason_title"),
        input: "textarea",
        inputLabel: this.$t("prv.refund_reason_label"),
        inputPlaceholder: this.$t("prv.refund_reason_placeholder"),
        inputAttributes: {
          "aria-label": this.$t("prv.refund_reason_label"),
          style:
            "min-height: 100px; resize: vertical; padding: 10px;margin:0 auto;width: 250px;",
        },
        showCancelButton: true,
        confirmButtonText: this.$t("prv.confirm_refund"),
        cancelButtonText: this.$t("common.cancel"),
        customClass: {
          popup: "no-flex-swal",
        },
        width: "600px",
        padding: "2em",
        backdrop: true,
        inputValidator: (value) => {
          if (!value) {
            return this.$t("prv.refund_reason_required");
          }
        },
      });

      if (reason) {
        Swal.fire({
          title: this.$t("prv.processing_refund"),
          allowOutsideClick: false,
          didOpen: () => Swal.showLoading(),
        });

        try {
          const res = await fetch(`${apiUrl}/live-sessions/refund`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${this.token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              slot_id: slotId,
              refund_reason: reason,
            }),
          });

          const json = await res.json();

          if (json.status === "success") {
            Swal.fire({
              icon: "success",
              title: this.$t("prv.refund_success_title"),
              text: json.message || this.$t("prv.refund_success_text"),
            });
            // يمكنك إعادة تحميل البيانات هنا إذا لزم الأمر
          } else {
            Swal.fire({
              icon: "warning",
              title: this.$t("prv.refund_fail_title"),
              text: json.message || this.$t("prv.refund_fail_text"),
            });
          }
        } catch (err) {
          Swal.fire({
            icon: "error",
            title: this.$t("common.error"),
            text: this.$t("prv.refund_error_text"),
          });
          console.error("Refund error:", err);
        }
      }
    },
    async openLiveSession(slotId) {
      const sessionData = await this.getLiveSessionToken(slotId);
      if (sessionData) {
        this.session = {
          token: sessionData.token,
          channelName: sessionData.channel_name,
          uid: sessionData.uid,
        };

        const modal = new bootstrap.Modal(
          document.getElementById("videoSessionModal")
        );
        modal.show();
      }
    },
    async getLiveSessionToken(slotId) {
      try {
        const res = await fetch(`${apiUrl}/live-sessions/get-token`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ slot_id: slotId }),
        });

        const json = await res.json();
        if (json.token) return json;
      } catch (e) {
        console.error("Failed to fetch token", e);
      }

      return null;
    },
    getDuration(start, end) {
      const s = new Date(start);
      const e = new Date(end);

      // لو أي واحد منهم invalid → رجع خط فاضي
      if (isNaN(s.getTime()) || isNaN(e.getTime())) {
        return "";
      }

      const diffMs = e.getTime() - s.getTime();
      const diffMin = Math.floor(diffMs / 1000 / 60);

      if (diffMin < 60) {
        return diffMin + " دقيقة";
      } else {
        const h = Math.floor(diffMin / 60);
        const m = diffMin % 60;
        return h + " ساعة" + (m ? " و " + m + " دقيقة" : "");
      }
    },
    async openLiveSessionInNewTab(
      slotId,
      instructor_uid,
      instructor_fname,
      instructor_lname
    ) {
      const sessionData = await this.getLiveSessionToken(slotId);
      let instructor_name = instructor_fname + " " + instructor_lname;
      if (sessionData) {
        // حفظ البيانات مؤقتًا (حتى لا نضع التوكين في URL)
        sessionStorage.setItem("liveSessionData", JSON.stringify(sessionData));
        const encodedUid = btoa(instructor_uid.toString());
        const encodedName = btoa(encodeURIComponent(instructor_name));

        window.open(
          "/session-private?slot=" +
            slotId +
            "&instructor_uid=" +
            encodedUid +
            "&instructor_name=" +
            encodedName,
          "_blank"
        );
      }
    },
    async getLiveSessionToken(slotId) {
      try {
        const res = await fetch(`${apiUrl}/live-sessions/get-token`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ slot_id: slotId }),
        });

        return await res.json();
      } catch (err) {
        console.error("Error getting token", err);
        return null;
      }
    },
  },
};
</script>

<style scoped>
.swal2-container.no-flex-swal {
  display: block !important;
  text-align: center;
}

/* المودال نفسه */
.no-flex-swal {
  position: absolute !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
}
</style>
